import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext.hook';

// Re-exporta o tipo canônico de etapa definido em diligenciaChecklists
export type { DiligenciaEtapa } from '@/utils/diligenciaChecklists';
import type { DiligenciaEtapa } from '@/utils/diligenciaChecklists';

export type DiligenciaStatus =
  | 'rascunho'
  | 'pendente'
  | 'em_analise'
  | 'doc_pendente'
  | 'aprovado'
  | 'reprovado'
  | 'cancelado';

export type AreaStatus = 'pendente' | 'em_analise' | 'concluida';

export interface Diligencia {
  id: string;
  incorporadora_id: string;

  // SPE fields
  spe_razao_social: string;
  spe_cnpj: string;
  spe_endereco_fiscal?: string;

  // Contato fields
  contato_representante_nome?: string;
  contato_representante_email?: string;
  contato_representante_telefone?: string;
  contato_financeiro_nome?: string;
  contato_financeiro_email?: string;
  contato_financeiro_telefone?: string;

  // Empreendimento fields
  empreendimento_nome: string;
  empreendimento_endereco?: string;
  empreendimento_numero?: string;
  empreendimento_cidade?: string;
  empreendimento_estado?: string;
  empreendimento_cep?: string;
  tipo_imovel?: string;
  terreno_matriculas?: string[];
  numero_unidades?: number;
  tamanho_unidades_m2?: string;
  data_prevista_lancamento?: string;
  data_prevista_entrega?: string;
  prazo_meses?: number;

  // Financeiro fields
  valor_captacao?: number;
  soma_m2_unidades?: number;
  soma_m2_vendavel?: number;
  valor_medio_m2_lancamento?: number;

  // Campos Bravaforte
  equity_vgv_vendavel?: number;
  desconto_efetivo?: number;
  valor_m2_bravaforte?: number;
  formato_retorno_scp?: string;

  // Workflow
  etapa_atual: DiligenciaEtapa;
  status: DiligenciaStatus;

  // Status por área de viabilidade
  area_tecnica_economica_status: AreaStatus;
  area_juridica_status: AreaStatus;
  area_contabil_status: AreaStatus;
  area_mercadologica_status: AreaStatus;

  // Signatario BravaForte (configuravel pelo admin)
  bravaforte_signatario_nome?: string | null;
  bravaforte_signatario_email?: string | null;

  // Meta
  imagem_url?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateDiligenciaInput {
  // SPE
  spe_razao_social: string;
  spe_cnpj: string;
  spe_endereco_fiscal?: string;

  // Contatos
  contato_representante_nome?: string;
  contato_representante_email?: string;
  contato_representante_telefone?: string;
  contato_financeiro_nome?: string;
  contato_financeiro_email?: string;
  contato_financeiro_telefone?: string;

  // Empreendimento
  empreendimento_nome: string;
  empreendimento_endereco?: string;
  empreendimento_numero?: string;
  empreendimento_cidade?: string;
  empreendimento_estado?: string;
  empreendimento_cep?: string;
  tipo_imovel?: string;
  terreno_matriculas?: string;
  numero_unidades?: string;
  tamanho_unidades_m2?: string;
  data_prevista_lancamento?: string;
  data_prevista_entrega?: string;
  prazo_meses?: string;

  // Financeiro
  valor_captacao?: string;
  soma_m2_unidades?: string;
  soma_m2_vendavel?: string;
  valor_medio_m2_lancamento?: string;
}

export interface UseDiligenciasOptions {
  /** Quando true, não executa fetchDiligencias automaticamente ao montar. Útil para admin. */
  skipAutoFetch?: boolean;
}

export const useDiligencias = (options?: UseDiligenciasOptions) => {
  const [diligencias, setDiligencias] = useState<Diligencia[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  /**
   * Busca a incorporadora_id do usuario atual
   */
  const getIncorporadoraId = useCallback(async (): Promise<string | null> => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('incorporadora_id')
        .eq('user_id', user.id)
        .maybeSingle(); // Use maybeSingle instead of single to handle 0 rows

      if (error) {
        console.warn('Erro ao buscar incorporadora_id:', error);
        return null;
      }

      return data?.incorporadora_id ?? null;
    } catch (err) {
      console.warn('Erro ao buscar incorporadora_id:', err);
      return null;
    }
  }, [user]);

  /**
   * Busca todas as diligencias do usuario
   */
  const fetchDiligencias = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      const incorporadoraId = await getIncorporadoraId();

      if (!incorporadoraId) {
        // Se nao tem incorporadora, busca pelo created_by
        const { data, error: fetchError } = await supabase
          .from('diligencias')
          .select('*')
          .eq('created_by', user.id)
          .order('created_at', { ascending: false });

        if (fetchError) throw fetchError;
        setDiligencias(data || []);
      } else {
        const { data, error: fetchError } = await supabase
          .from('diligencias')
          .select('*')
          .eq('incorporadora_id', incorporadoraId)
          .order('created_at', { ascending: false });

        if (fetchError) throw fetchError;
        setDiligencias(data || []);
      }
    } catch (err) {
      console.error('Erro ao buscar diligencias:', err);
      setError('Erro ao carregar diligências');
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar as diligências.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [user, toast, getIncorporadoraId]);

  /**
   * Cria uma nova diligencia
   */
  const createDiligencia = async (input: CreateDiligenciaInput): Promise<Diligencia | null> => {
    if (!user) {
      toast({
        title: 'Erro',
        description: 'Usuário não autenticado.',
        variant: 'destructive',
      });
      return null;
    }

    setIsLoading(true);

    try {
      // Buscar incorporadora_id do usuario
      const incorporadoraId = await getIncorporadoraId();

      // Preparar dados para inserção
      const insertData = {
        incorporadora_id: incorporadoraId, // Pode ser null se usuario nao tiver incorporadora
        created_by: user.id,

        // SPE
        spe_razao_social: input.spe_razao_social,
        spe_cnpj: input.spe_cnpj,
        spe_endereco_fiscal: input.spe_endereco_fiscal || null,

        // Contatos
        contato_representante_nome: input.contato_representante_nome || null,
        contato_representante_email: input.contato_representante_email || null,
        contato_representante_telefone: input.contato_representante_telefone || null,
        contato_financeiro_nome: input.contato_financeiro_nome || null,
        contato_financeiro_email: input.contato_financeiro_email || null,
        contato_financeiro_telefone: input.contato_financeiro_telefone || null,

        // Empreendimento
        empreendimento_nome: input.empreendimento_nome,
        empreendimento_endereco: input.empreendimento_endereco || null,
        empreendimento_numero: input.empreendimento_numero || null,
        empreendimento_cidade: input.empreendimento_cidade || null,
        empreendimento_estado: input.empreendimento_estado || null,
        empreendimento_cep: input.empreendimento_cep || null,
        tipo_imovel: input.tipo_imovel || null,
        terreno_matriculas: input.terreno_matriculas
          ? input.terreno_matriculas.split(',').map(m => m.trim())
          : null,
        numero_unidades: input.numero_unidades ? parseInt(input.numero_unidades) : null,
        tamanho_unidades_m2: input.tamanho_unidades_m2 || null,
        data_prevista_lancamento: input.data_prevista_lancamento || null,
        data_prevista_entrega: input.data_prevista_entrega || null,
        prazo_meses: input.prazo_meses ? parseInt(input.prazo_meses) : null,

        // Financeiro
        valor_captacao: input.valor_captacao ? parseFloat(input.valor_captacao) : null,
        soma_m2_unidades: input.soma_m2_unidades ? parseFloat(input.soma_m2_unidades) : null,
        soma_m2_vendavel: input.soma_m2_vendavel ? parseFloat(input.soma_m2_vendavel) : null,
        valor_medio_m2_lancamento: input.valor_medio_m2_lancamento ? parseFloat(input.valor_medio_m2_lancamento) : null,

        // Workflow - nova diligencia sempre comeca em nova_solicitacao com status pendente
        etapa_atual: 'nova_solicitacao' as DiligenciaEtapa,
        status: 'pendente' as DiligenciaStatus,
      };

      const { data, error: insertError } = await supabase
        .from('diligencias')
        .insert(insertData)
        .select()
        .single();

      if (insertError) throw insertError;

      toast({
        title: 'Solicitação enviada!',
        description: 'Sua solicitação de diligência foi cadastrada com sucesso.',
      });

      // Atualizar lista local
      setDiligencias(prev => [data, ...prev]);

      return data;
    } catch (err) {
      console.error('Erro ao criar diligencia:', err);
      toast({
        title: 'Erro ao enviar',
        description: 'Não foi possível enviar sua solicitação. Tente novamente.',
        variant: 'destructive',
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Busca uma diligencia por ID
   */
  const getDiligenciaById = async (id: string): Promise<Diligencia | null> => {
    try {
      const { data, error: fetchError } = await supabase
        .from('diligencias')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;
      return data;
    } catch (err) {
      console.error('Erro ao buscar diligencia:', err);
      return null;
    }
  };

  /**
   * Atualiza o status de uma diligencia
   */
  const updateDiligenciaStatus = async (
    id: string,
    status: DiligenciaStatus
  ): Promise<boolean> => {
    setIsLoading(true);
    try {
      const { error: updateError } = await supabase
        .from('diligencias')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (updateError) throw updateError;

      // Atualizar lista local
      setDiligencias(prev =>
        prev.map(d => (d.id === id ? { ...d, status } : d))
      );

      toast({
        title: 'Status atualizado',
        description: 'O status da diligência foi atualizado.',
      });

      return true;
    } catch (err) {
      console.error('Erro ao atualizar status:', err);
      toast({
        title: 'Erro',
        description: 'Não foi possível atualizar o status.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Atualiza a etapa de uma diligencia
   */
  const updateDiligenciaEtapa = async (
    id: string,
    etapa: DiligenciaEtapa
  ): Promise<boolean> => {
    setIsLoading(true);
    try {
      const { error: updateError } = await supabase
        .from('diligencias')
        .update({
          etapa_atual: etapa,
          status: 'pendente', // Reset status quando muda de etapa
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (updateError) throw updateError;

      // Atualizar lista local
      setDiligencias(prev =>
        prev.map(d => (d.id === id ? { ...d, etapa_atual: etapa, status: 'pendente' } : d))
      );

      toast({
        title: 'Etapa atualizada',
        description: 'A diligência avançou para a próxima etapa.',
      });

      return true;
    } catch (err) {
      console.error('Erro ao atualizar etapa:', err);
      toast({
        title: 'Erro',
        description: 'Não foi possível atualizar a etapa.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Busca TODAS as diligencias (para admin)
   */
  const fetchAllDiligencias = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('diligencias')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setDiligencias(data || []);
    } catch (err) {
      console.error('Erro ao buscar diligencias:', err);
      setError('Erro ao carregar diligências');
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar as diligências.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  /**
   * Avanca a diligencia para a proxima etapa
   */
  const avancarEtapa = async (id: string): Promise<boolean> => {
    const etapas: DiligenciaEtapa[] = [
      'nova_solicitacao',
      'viabilidade_tecnica_economica',
      'viabilidade_juridica',
      'viabilidade_contabil',
      'viabilidade_mercadologica',
      'relatorio',
    ];

    // Buscar diligencia atual
    const diligencia = diligencias.find(d => d.id === id);
    if (!diligencia) {
      toast({
        title: 'Erro',
        description: 'Diligência não encontrada.',
        variant: 'destructive',
      });
      return false;
    }

    const currentIndex = etapas.indexOf(diligencia.etapa_atual);
    if (currentIndex === etapas.length - 1) {
      toast({
        title: 'Aviso',
        description: 'A diligência já está na última etapa.',
        variant: 'destructive',
      });
      return false;
    }

    const nextEtapa = etapas[currentIndex + 1];
    return updateDiligenciaEtapa(id, nextEtapa);
  };

  /**
   * Retrocede a diligencia para a etapa anterior
   */
  const retrocederEtapa = async (id: string): Promise<boolean> => {
    const etapas: DiligenciaEtapa[] = [
      'nova_solicitacao',
      'viabilidade_tecnica_economica',
      'viabilidade_juridica',
      'viabilidade_contabil',
      'viabilidade_mercadologica',
      'relatorio',
    ];

    // Buscar diligencia atual
    const diligencia = diligencias.find(d => d.id === id);
    if (!diligencia) {
      toast({
        title: 'Erro',
        description: 'Diligência não encontrada.',
        variant: 'destructive',
      });
      return false;
    }

    const currentIndex = etapas.indexOf(diligencia.etapa_atual);
    if (currentIndex === 0) {
      toast({
        title: 'Aviso',
        description: 'A diligência já está na primeira etapa.',
        variant: 'destructive',
      });
      return false;
    }

    const prevEtapa = etapas[currentIndex - 1];
    return updateDiligenciaEtapa(id, prevEtapa);
  };

  // Carregar diligencias quando o usuario mudar (desabilitável via skipAutoFetch)
  useEffect(() => {
    if (user && !options?.skipAutoFetch) {
      fetchDiligencias();
    }
  }, [user, fetchDiligencias, options?.skipAutoFetch]);

  return {
    diligencias,
    isLoading,
    error,
    fetchDiligencias,
    fetchAllDiligencias,
    createDiligencia,
    getDiligenciaById,
    updateDiligenciaStatus,
    updateDiligenciaEtapa,
    avancarEtapa,
    retrocederEtapa,
  };
};
